/**
 * @module experience/one-time-payment
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
import { ORDER_SUMMARY_CONSTANT } from '@experience/order-summary/order-summary.constants';
import {
  OrderAttachmentDocument,
  OrderSummaryInterface,
  PaymentMethods,
  PriceCollection,
  OrderItemDetails,
} from '@support/api/proxies/responses/api-2814-new.interface';
import { ORDER_TYPE } from '@experience/shop/shop.constants';
import { DocumentObject } from '@experience/order-summary/interfaces/order-summary-new.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

export class OneTimePaymentConfirmation {
  /**
   * order confirmation sent to msisdn
   */
  public confirmationSentTo: string;
  /**
   * order confirmation sent to email
   */
  public optionalEmailSentTo: string;
  /**
   * confirmation id
   */
  public orderConfirmationId: string;
  /**
   * total amount paid
   */
  public totalAmount: number;
  /**
   * subtotal amount paid
   */
  public subTotal: number;
  /**
   * payment zipCode
   */
  public zipCode: string;
  /**
   * payment countryCode
   */
  public country: string;
  /**
   * total amount paid
   */
  public totalPayment: number;
  /**
   * taxes applied for amount paid
   */
  public taxesAndFees: number;
  /**
   * customer Id
   */
  public customerId: string;
  /**
   * account Id
   */
  public accountId: string;
  /**
   * payment date
   */
  public paymentDate: string;
  /**
   * payment card number
   */
  public cardNumber: string;
  /**
   * payment card type
   */
  public cardType: string;
  /**
   * payment card type
   */
  public paymentMethod: string;
  /**
   * payment card holder name
   */
  public nameOnCard: string;
  /**
   * availableExpiration
   */
  public availableExpiration: string;
  /**
   * order Items
   */
  public orderItems: OrderItemDetails;
  /**
   * Stores the information for receipt and transaction confirmation docs
   */
  public documentObject: DocumentObject;

  public accountBalance: string;

  constructor(
    orderInformation: OrderSummaryInterface,
    private objectService?: ObjectService
  ) {
    this.documentObject = {};
    if (orderInformation) {
      this.orderItems = orderInformation.orderItems;
      this.paymentAmount(orderInformation);
      this.orderConfirmationId = orderInformation.orderId;
      this.accountId = orderInformation.accountId;
      this.paymentDate = orderInformation.orderDate ? orderInformation.orderDate.display : '';
      this.confirmationSentTo = orderInformation.email;
      this.accountBalance = this.objectService.get(orderInformation, 'accountBalance.display', '');
      this.setPaymentMethod(orderInformation.paymentMethods);
      this.fetchReceiptAndConfirmation(ORDER_TYPE.RECEIPT, 'receiptId', orderInformation);
      this.fetchReceiptAndConfirmation(
        ORDER_TYPE.TRANSACTION_CONFIRMATION,
        'confirmationId',
        orderInformation
      );
    }
  }

  /**
   * Method to set document Ids to be used to retreive documents
   * @param documentType : type of document - Receipt, confirmation
   * @param docId : Document Id of the required document
   * @param orderInformation
   * @returns void
   */
  private fetchReceiptAndConfirmation(
    documentType: string,
    docId: string,
    orderInformation: OrderSummaryInterface
  ): void {
    if (orderInformation.documents) {
      const docAttachment = orderInformation.documents.filter(
        (attachment: OrderAttachmentDocument) => {
          return attachment.documentId && attachment.documentType === documentType;
        }
      );
      this.documentObject[docId] =
        docAttachment.length > 0 && docAttachment[0].documentId
          ? docAttachment[0].documentId
          : null;
    }
  }

  /**
   * Fetch the price details
   * @returns void
   */
  private paymentAmount(orderInformation: OrderSummaryInterface): void {
    if (orderInformation.priceCollection) {
      orderInformation.priceCollection.forEach((priceDetails: PriceCollection) => {
        switch (priceDetails.priceType) {
          case ORDER_SUMMARY_CONSTANT.SUB_TOTAL:
            this.subTotal = priceDetails.priceEntity.amount;
            break;
          case ORDER_SUMMARY_CONSTANT.TAX_AND_FEE:
            this.taxesAndFees = priceDetails.priceEntity.amount;
            break;
          case ORDER_SUMMARY_CONSTANT.TOTAL:
            this.totalAmount = priceDetails.priceEntity.amount;
            break;
          default: {
          }
        }
      });
    }
  }

  /**
   * set payment details
   * @param {PaymentMethod[]} paymentMethods
   */
  private setPaymentMethod(paymentMethods: PaymentMethods[]): void {
    if (paymentMethods && paymentMethods.length) {
      this.paymentMethod = paymentMethods[0].paymentDetails.paymentMethodCode;
      const maskedCardNumber = paymentMethods[0].paymentDetails.maskedCardNumber;
      this.cardNumber = maskedCardNumber ? maskedCardNumber.replace(/\W(?=\W{4})/g, '') : '';
      this.cardType = paymentMethods[0].paymentDetails.typeCode;
      this.nameOnCard = paymentMethods[0].paymentDetails.nameOnCard;
      this.zipCode =
        paymentMethods[0].paymentDetails &&
        paymentMethods[0].paymentDetails.address &&
        paymentMethods[0].paymentDetails.address.zipCode
          ? paymentMethods[0].paymentDetails.address.zipCode
          : '';
      this.country =
        paymentMethods[0].paymentDetails && paymentMethods[0].paymentDetails.countryName
          ? paymentMethods[0].paymentDetails.countryName
          : '';
    }
  }
}
